import { useCallback, useRef, useState } from "react";
import {
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  MapPin,
  PlaySquare,
  Users,
  Video,
} from "lucide-react";
import toast from "react-hot-toast";
import CourtFilterBar from "../../components/common/CourtFilterBar";
import type { CourtFilterValues } from "../../components/common/CourtFilterBar";
import type { VideoStreamCard } from "../../components/common/VideoCard";
import { getPublicPrejoinMeetingsApi } from "../../api/publicMeetingJoinApi";

// Court-room rows extend the filter-bar card shape so CourtFilterBar can filter them.
// Only court-room level info is shown — no cause list.
interface CourtRoom extends VideoStreamCard {
  courtRoomName: string;
  courtName: string;
  hierarchy: string;
  meetingType: "High Court" | "District Court";
  webinarNumber: string;
  password: string;
  vcLink: string;
  streamLink: string;
  rawStatus?: string;
}

const PAGE_HEADER = {
  name: "Court Meetings",
  state: "High Court and District Court public meeting rooms",
  logo: "/logo.png",
};

// Shape returned by POST /api/public-meeting/meetings (BenchRoom docs).
interface ApiBenchRoom {
  _id: string;
  bench?: {
    _id?: string;
    benchName?: string;
    location?: string;
    highcourt?: {
      highCourtName?: string;
      stateName?: string;
      stateCode?: string;
    };
  };
  benchName?: string;
  courtRoom?: string;
  floorNumber?: string | number;
  status?: string;
  meetingId?: string;
  meetingPassword?: string;
  meetingToken?: string;
}

interface ApiCourtRoom {
  _id: string;
  courtRoomName?: string;
  roomNumber?: string;
  floorNumber?: string | number;
  status?: string;
  courtType?: string;
  highcourt?: {
    highCourtName?: string;
    stateName?: string;
    stateCode?: string;
  };
  district?: {
    districtName?: string;
    stateName?: string;
    headquarter?: string;
  };
  complex?: {
    complexName?: string;
    address?: string;
  };
  establishment?: {
    name?: string;
    establishmentType?: string;
  };
  meetingId?: string;
  meetingPassword?: string;
  meetingToken?: string;
}

const joinParts = (parts: Array<string | number | undefined>) =>
  parts.filter(Boolean).join(" - ");

const getJoinLink = (meetingToken?: string) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return meetingToken ? `${origin}/join-session?mid=${meetingToken}` : "";
};

const mapHighCourtRoom = (b: ApiBenchRoom): CourtRoom => {
  const highCourtName = b.bench?.highcourt?.highCourtName || "High Court";
  const benchName = b.bench?.benchName || "";
  const courtHallName = b.benchName || "";
  const roomName = b.courtRoom || courtHallName || "Court Room";
  const hierarchy = joinParts([
    highCourtName,
    benchName,
    courtHallName !== benchName ? courtHallName : undefined,
    b.floorNumber && `${b.floorNumber} Floor`,
  ]);

  return {
    id: b._id,
    courtRoomName: roomName,
    courtName: highCourtName,
    hierarchy,
    meetingType: "High Court",
    webinarNumber: b.meetingId || "",
    password: b.meetingPassword || "",
    vcLink: getJoinLink(b.meetingToken),
    streamLink: `/watch?room=${b._id}`,
    rawStatus: b.status,
    status: "Live",
    courtType: "High Court",
    room: b.courtRoom,
    bench: benchName,
    highCourt: highCourtName,
    state: b.bench?.highcourt?.stateName,
    // Fields required by the filter-bar card shape (not displayed):
    caseNo: roomName,
    title: `${roomName} ${hierarchy}`,
    court: highCourtName,
  };
};

const mapDistrictRoom = (room: ApiCourtRoom): CourtRoom => {
  const districtName = room.district?.districtName || "District Court";
  const stateName = room.district?.stateName || room.highcourt?.stateName || "";
  const courtName =
    districtName === "District Court"
      ? "District Court"
      : `District Court, ${districtName}`;
  const roomName =
    joinParts([room.roomNumber, room.courtRoomName]) ||
    room.courtRoomName ||
    "Court Room";
  const hierarchy = joinParts([
    courtName,
    room.complex?.complexName,
    room.establishment?.name,
    room.floorNumber && `${room.floorNumber} Floor`,
  ]);

  return {
    id: room._id,
    courtRoomName: roomName,
    courtName,
    hierarchy,
    meetingType: "District Court",
    webinarNumber: room.meetingId || "",
    password: room.meetingPassword || "",
    vcLink: getJoinLink(room.meetingToken),
    streamLink: `/watch?room=${room._id}`,
    rawStatus: room.status,
    status: "Live",
    courtType: "District Court",
    room: roomName,
    state: stateName,
    district: districtName,
    courtComplex: room.complex?.complexName,
    courtEstablishment: room.establishment?.name,
    caseNo: roomName,
    title: `${roomName} ${hierarchy}`,
    court: courtName,
  };
};

const Meetings = () => {
  const [rooms, setRooms] = useState<CourtRoom[]>([]);
  const [filtered, setFiltered] = useState<VideoStreamCard[]>([]);
  const [loading, setLoading] = useState(true);
  const lastMeetingFilterKeyRef = useRef("");

  const handleFetch = useCallback(async (filters?: CourtFilterValues) => {
    setLoading(true);
    try {
      const payload =
        filters?.courtType === "High Court"
          ? {
              courtType: "High Court",
              highcourt:
                filters.highCourt !== "All" ? filters.highCourt : undefined,
              highCourtName:
                filters.highCourtLabel !== "All"
                  ? filters.highCourtLabel
                  : undefined,
              bench: filters.bench !== "All" ? filters.bench : undefined,
              benchName:
                filters.benchLabel !== "All" ? filters.benchLabel : undefined,
            }
          : filters?.courtType === "District Court"
            ? { courtType: "District Court" }
            : {};
      const res = await getPublicPrejoinMeetingsApi(payload);
      if (res?.data?.status) {
        const highCourtList: ApiBenchRoom[] = res.data.highcourt || [];
        const districtList: ApiCourtRoom[] = res.data.district || [];
        const mapped = [
          ...highCourtList.map(mapHighCourtRoom),
          ...districtList.map(mapDistrictRoom),
        ];
        setRooms(mapped);
        setFiltered(mapped);
      } else {
        toast.error(res?.data?.message || "Failed to load meetings");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load meetings",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFilterChange = useCallback(
    (filters: CourtFilterValues) => {
      const filterKey = [
        filters.courtType,
        filters.highCourt,
        filters.bench,
      ].join(":");

      if (filterKey === lastMeetingFilterKeyRef.current) return;

      lastMeetingFilterKeyRef.current = filterKey;
      handleFetch(filters);
    },
    [handleFetch],
  );

  return (
    <div className="mx-auto  px-3 py-4 sm:px-5 sm:py-6">
      {/* Meetings header */}
      <div className="mb-5 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:gap-4 sm:p-5">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-50 sm:h-14 sm:w-14">
          <img
            src={PAGE_HEADER.logo}
            alt="Court logo"
            className="h-9 w-9 object-contain sm:h-11 sm:w-11"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        </div>
        <div className="min-w-0">
          <h1 className="truncate text-base font-bold text-slate-900 sm:text-xl">
            {PAGE_HEADER.name}
          </h1>
          <p className="flex items-center gap-1 text-xs text-slate-500 sm:text-sm">
            <MapPin className="h-3.5 w-3.5" /> {PAGE_HEADER.state}
          </p>
        </div>
        <div className="ml-auto hidden rounded-xl bg-slate-50 px-4 py-2 text-right sm:block">
          <p className="text-lg font-bold text-slate-900">{rooms.length}</p>
          <p className="text-xs text-slate-500">Meeting rooms</p>
        </div>
      </div>

      {/* Filter */}
      <CourtFilterBar
        rows={rooms}
        onChange={setFiltered}
        onFiltersChange={handleFilterChange}
      />

      {loading ? (
        <div className="mt-5 flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-16">
          <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          {/* Mobile / tablet: cards */}
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:hidden">
            {(filtered as CourtRoom[]).map((room) => (
              <RoomCard key={room.id} room={room} />
            ))}
          </div>

          {/* Desktop: table */}
          {filtered.length > 0 && (
            <div className="mt-5 hidden lg:block">
              <RoomTable rooms={filtered as CourtRoom[]} />
            </div>
          )}

          {filtered.length === 0 && (
            <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
              <p className="text-sm font-semibold text-slate-700">
                No court rooms match.
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {rooms.length === 0
                  ? "No meetings are available right now."
                  : "Try clearing the filters above."}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Meetings;

/* ---------------- shared helpers ---------------- */

const copyText = (label: string, value: string) => {
  if (!value) {
    toast.error(`${label} not available`);
    return;
  }

  navigator.clipboard
    .writeText(value)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.error("Copy failed"));
};

const openTab = (url: string) => {
  if (!url) {
    toast.error("Link not available");
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
};

const StatusBadge = ({ status }: { status: string }) => {
  const live = status === "Live";
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
        live ? "bg-red-500 text-white" : "bg-slate-200 text-slate-600"
      }`}
    >
      {live && (
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
      )}
      {status}
    </span>
  );
};

const IconBtn = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100"
  >
    {children}
  </button>
);

/* ---------------- Desktop table ---------------- */

function RoomTable({ rooms }: { rooms: CourtRoom[] }) {
  const [revealed, setRevealed] = useState<Record<string | number, boolean>>(
    {},
  );
  const toggle = (id: string | number) =>
    setRevealed((r) => ({ ...r, [id]: !r[id] }));

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full min-w-[1120px] text-left text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Court</th>
            <th className="px-4 py-3">Court Room</th>
            <th className="px-4 py-3">Meeting Number</th>
            <th className="px-4 py-3">Password</th>
            <th className="px-4 py-3">VC Link</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rooms.map((room) => (
            <tr key={room.id} className="hover:bg-slate-50/60">
              <td className="px-4 py-3">
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                    room.meetingType === "High Court"
                      ? "bg-blue-50 text-blue-700"
                      : "bg-orange-50 text-orange-700"
                  }`}
                >
                  {room.meetingType}
                </span>
                <p className="mt-1 font-semibold text-slate-900">
                  {room.courtName}
                </p>
                {room.state && (
                  <p className="text-xs text-slate-500">{room.state}</p>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">
                    {room.courtRoomName}
                  </span>
                </div>
                {room.bench && (
                  <p className="mt-0.5 text-xs text-slate-500">
                    Bench: {room.bench}
                  </p>
                )}
              </td>

              <td className="px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono font-semibold text-slate-900">
                    {room.webinarNumber || "—"}
                  </span>
                  <IconBtn
                    label="Copy Meeting Id"
                    onClick={() => copyText("Meeting Id", room.webinarNumber)}
                  >
                    <Copy className="h-4 w-4" />
                  </IconBtn>
                </div>
              </td>

              <td className="px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono font-semibold text-slate-900">
                    {revealed[room.id] ? room.password : "••••••"}
                  </span>
                  <IconBtn
                    label="Toggle password"
                    onClick={() => toggle(room.id)}
                  >
                    {revealed[room.id] ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </IconBtn>
                  <IconBtn
                    label="Copy password"
                    onClick={() => copyText("Password", room.password)}
                  >
                    <Copy className="h-4 w-4" />
                  </IconBtn>
                </div>
              </td>

              <td className="px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => openTab(room.vcLink)}
                    disabled={!room.vcLink}
                    className="max-w-[200px] truncate text-blue-600 hover:underline"
                    title={room.vcLink}
                  >
                    {room.vcLink ? "VC Link" : "No Link"}
                  </button>
                  <IconBtn
                    label="Copy VC link"
                    onClick={() => copyText("VC link", room.vcLink)}
                  >
                    <Copy className="h-4 w-4" />
                  </IconBtn>
                </div>
              </td>

              <td className="px-4 py-3">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => openTab(room.vcLink)}
                    disabled={!room.vcLink}
                    className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                  >
                    <Users className="h-3.5 w-3.5" /> Join
                  </button>
                  {/* <button
                      type="button"
                      onClick={() => openTab(room.streamLink)}
                      disabled={!live}
                      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                        live
                          ? "bg-slate-900 text-white hover:bg-slate-800"
                          : "cursor-not-allowed bg-slate-100 text-slate-400"
                      }`}
                    >
                      <PlaySquare className="h-3.5 w-3.5" /> Stream
                    </button> */}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Mobile card ---------------- */

function RoomCard({ room }: { room: CourtRoom }) {
  const [showPassword, setShowPassword] = useState(false);
  const live = room.status === "Live";

  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 text-white">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-bold">{room.courtRoomName}</h3>
          <p className="truncate text-xs text-white/80">{room.courtName}</p>
        </div>
        <StatusBadge status={room.status} />
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="rounded-xl bg-slate-50 px-3 py-2">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
            Hierarchy
          </p>
          <p className="mt-0.5 text-sm font-semibold text-slate-800">
            {room.hierarchy || "—"}
          </p>
        </div>

        {/* Meeting Id */}
        <div className="flex items-center gap-3">
          <span className="text-slate-400">
            <Video className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              Meeting Id
            </p>
            <p className="truncate font-mono text-sm font-semibold text-slate-900">
              {room.webinarNumber || "—"}
            </p>
          </div>
          <IconBtn
            label="Copy Meeting Id"
            onClick={() => copyText("Meeting Id", room.webinarNumber)}
          >
            <Copy className="h-4 w-4" />
          </IconBtn>
        </div>

        {/* Password */}
        <div className="flex items-center gap-3">
          <span className="text-slate-400">
            <KeyRound className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              Password
            </p>
            <p className="font-mono text-sm font-semibold text-slate-900">
              {showPassword ? room.password : "••••••"}
            </p>
          </div>
          <IconBtn
            label="Toggle password"
            onClick={() => setShowPassword((v) => !v)}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </IconBtn>
          <IconBtn
            label="Copy password"
            onClick={() => copyText("Password", room.password)}
          >
            <Copy className="h-4 w-4" />
          </IconBtn>
        </div>

        {/* VC link */}
        <div className="flex items-center gap-3">
          <span className="text-slate-400">
            <ExternalLink className="h-4 w-4" />
          </span>
          <button
            type="button"
            onClick={() => openTab(room.vcLink)}
            disabled={!room.vcLink}
            className="min-w-0 flex-1 text-left"
          >
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              VC Link
            </p>
            <p className="truncate text-sm font-semibold text-blue-600 hover:underline">
              {room.vcLink || "No link available"}
            </p>
          </button>
          <IconBtn
            label="Copy VC link"
            onClick={() => copyText("VC link", room.vcLink)}
          >
            <Copy className="h-4 w-4" />
          </IconBtn>
        </div>
      </div>

      <div className="flex gap-2 border-t border-slate-100 p-3">
        <button
          type="button"
          onClick={() => openTab(room.vcLink)}
          disabled={!room.vcLink}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
        >
          <Users className="h-4 w-4" /> Join
        </button>
        <button
          type="button"
          onClick={() => openTab(room.streamLink)}
          disabled={!live}
          className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
            live
              ? "bg-slate-900 text-white hover:bg-slate-800"
              : "cursor-not-allowed bg-slate-100 text-slate-400"
          }`}
        >
          <PlaySquare className="h-4 w-4" /> View Stream
        </button>
      </div>
    </article>
  );
}
